import { z } from "zod";

/**
 * Editorial voice block on a concierge answer — the model-written layer of an
 * editorial answer page (spec: docs/designs/concierge-editorial-answers-spec.md).
 *
 * Only words live here. Prices, titles, images and slugs come from the
 * grounded catalog rows on the same response; a renderer must never source a
 * fact from this block.
 *
 * Length caps are part of the contract: the mobile layouts are designed
 * around them (kicker is an overline, headline is one display line-pair,
 * lede is 1–2 sentences). The API clamps model output to fit before
 * validating, so a response that reaches a client always passes this schema.
 */
export const editorialBlockSchema = z.object({
  /** Small overline, e.g. "YOUR WEEKEND, SHAPED". */
  kicker: z.string().min(1).max(28),
  /** One sentence of voice, no facts. */
  headline: z.string().min(1).max(90),
  /** 1–2 sentences of orientation. No prices, no venue claims. */
  lede: z.string().min(1).max(240),
  /**
   * Per-day theme lines, index-aligned with `itinerary.days`. Never longer
   * than the day list; the API drops the whole array when the model
   * miscounts. Itinerary answers only.
   */
  dayThemes: z.array(z.string().min(1).max(40)).optional(),
  /**
   * 0–3 practical fact pills, each derivable from the grounded rows.
   * Drop a fact rather than invent one.
   */
  facts: z.array(z.string().min(1).max(60)).max(3).optional(),
  /**
   * Themed groupings of the grounded result slugs, qa/theme answers only —
   * "Best first picks", "Splash and play". Every slug must exist in the
   * response's `matches`; the API filters, so a client can index blindly.
   * Absent → render the classic single rail.
   */
  /**
   * One warm sentence about the trip's pacing ("busy mornings, slow
   * afternoons"). Voice only; itinerary answers only.
   */
  rhythm: z.string().max(280).optional(),
  /**
   * Per-day stepper cards, index-aligned with itinerary.days: a short title
   * (usually the theme), an effort tag ("Half day"), and a one-line blurb.
   * Dropped whole when the model miscounts, like dayThemes.
   */
  daySummaries: z
    .array(
      z.object({
        title: z.string().min(1).max(40),
        tag: z.string().max(30).optional(),
        blurb: z.string().max(120).optional(),
      }),
    )
    .max(14)
    .optional(),
  /**
   * Well-known public places and practical local advice the model may add
   * from general knowledge when the catalog does not carry them (owner
   * decision 2026-08-11). Strictly non-commercial: landmarks, squares,
   * beaches, neighbourhoods, habits. The API rejects any entry containing a
   * digit, which is a blunt but effective ban on invented prices, hours and
   * dates. Rendered apart from catalog rows, never with a Reserve action.
   */
  localKnowledge: z
    .array(
      z.object({
        title: z.string().min(1).max(50),
        note: z.string().min(1).max(160),
      }),
    )
    .max(4)
    .optional(),
  /*
   * Bounds here are the outer limit, not the editorial shape.
   *
   * The model's own shelves are held to 4 shelves of 6 by the sanitiser in
   * ai.service.ts, which is where the magazine-spread rule lives. These are
   * looser only to leave room for the one shelf we append ourselves — the
   * catch-all carrying retrieved places the model left off every shelf, which
   * is a sweep-up rather than a theme and so is allowed to be longer.
   *
   * Getting this wrong is expensive and silent: parseEditorialReply returns
   * the un-editorialised reply whole when this parse fails, so a section one
   * slug over the limit costs the traveller the entire block — headline, lede,
   * facts and every shelf — with nothing logged.
   */
  sections: z
    .array(
      z.object({
        title: z.string().min(1).max(40),
        sub: z.string().max(120).optional(),
        slugs: z.array(z.string()).min(1).max(12),
      }),
    )
    .max(5)
    .optional(),
  /**
   * One line per place, keyed by slug: why this one, for this traveller.
   *
   * The competitor answer that scored 78 to our 55 is barely prose at all. Each
   * place is a card carrying its own sentence, and the writing between them is
   * only group headings and a closing recommendation:
   *
   *   Jemaa el-Fna, Attraction
   *   "Go around sunset into evening for the full atmosphere, performers,
   *    storytellers, and food stalls."
   *
   * We ask for the opposite: one long paragraph that names the places,
   * describes them and prices them. That paragraph is where all 31 remaining
   * catalogue leaks live, because it is the only place with room to narrate a
   * shelf. The fix is not to forbid the narration, which this feature has
   * failed at six times, but to move the job into a field so the paragraph has
   * less to do.
   *
   * A flat map rather than a field on `sections`, for two reasons: the section
   * shape is what both clients already read and must not break, and 43 of 100
   * answers carry no sections at all while every one of them carries cards.
   *
   * Keyed on slug and filtered against the turn's own matches, so a line about
   * a place we did not retrieve is dropped rather than rendered under someone
   * else's card.
   */
  why: z.record(z.string(), z.string().min(1).max(140)).optional(),
  /**
   * Slugs of places the turn found whose OWN details contradict what the
   * traveller asked: a lunch-only restaurant in a dinner-show list (owner,
   * 2026-09-24). The API keeps them off every shelf and drops them from the
   * cards, then strips this field, so a client never has to read it.
   */
  offAsk: z.array(z.string()).max(12).optional(),
  /**
   * The three moves a good concierge makes on every answer, as slots the model
   * fills rather than as sentences it is asked to write.
   *
   * Measured 2026-08-13 against a competitor over five questions: they scored
   * 78 and we scored 55, and on the widest question, a week in Morocco, they
   * scored 86 to our 32. Their catalogue is not better than ours and their
   * route was built from public knowledge, not inventory. The difference was
   * habit, and the same three habits on every answer: they commit to one
   * recommendation, they name the downside a brochure would leave out, and
   * they close with a concrete first move.
   *
   * Our prompt has asked for all three in prose for weeks. Asking does not
   * hold. Over a single day, six rules written as instructions were each
   * routed around inside one run, while every fix that changed the SHAPE of
   * what the model produces held. So these are fields, not requests: a field
   * left empty is visibly empty and can be counted, where a sentence quietly
   * dropped is invisible.
   *
   * Voice and judgement only, like everything else in this block. Prices and
   * hours come from the grounded rows, never from here.
   */
  /** The commit: what you would actually do, and why, in one sentence. */
  pick: z.string().min(1).max(180).optional(),
  /** The honest downside, the part a friend mentions and a brochure does not. */
  caution: z.string().min(1).max(180).optional(),
  /** One concrete next step: "Late afternoon the garden, sunset the square." */
  firstMove: z.string().min(1).max(180).optional(),
  /**
   * What the trip costs AROUND the things we sell, and the only place in this
   * block that may hold a number we did not supply.
   *
   * Owner decision 2026-08-20, after watching a competitor answer "how much
   * could this cost me?" with a four-line table and a total. We answered the
   * same question with per-stop prices and no trip figure, because the rules
   * here ban an invented price outright — a ban written for a real incident,
   * where the concierge told a traveller there was no deposit while the villa
   * they were reading took a non-refundable 40%.
   *
   * The line the owner drew, and the reason this field can exist safely: a
   * price for something WE SELL is never estimated, and everything else may
   * be. Accommodation, meals out, getting around and spending money are none
   * of them ours; the stops on the cards are, and they carry real prices from
   * the catalogue. An estimate written over a real price is a wrong price, so
   * `lines` must never name a place on the rail.
   *
   * Ranges rather than figures, and `basis` on every one, because an estimate
   * that does not say who it is for reads as a quote. The renderer labels the
   * whole block as an estimate; nothing in it may be shown as though we had
   * checked it.
   */
  estimate: z
    .object({
      lines: z
        .array(
          z.object({
            label: z.string().min(1).max(40),
            range: z.string().min(1).max(40),
          }),
        )
        .min(1)
        .max(4),
      total: z.string().min(1).max(40),
      /** Who and how long it assumes: "2 adults, 3 nights". */
      basis: z.string().min(1).max(80),
    })
    .optional(),
});

export type EditorialBlock = z.infer<typeof editorialBlockSchema>;

/* ------------------------------------------------------------------ */
/*  Trip recaps (spec step 3) — accounts-only                          */
/* ------------------------------------------------------------------ */

/** One card on the home "Recaps" rail. */
export const recapCardSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** Server-localized relative label ("Yesterday"), Morocco TZ. */
  when: z.string(),
  /** Catalog hero of the landed pick (else first shortlist hero). Never stock. */
  imageUrl: z.string().nullable(),
  /** Last user message, truncated. */
  tail: z.string(),
});
export type RecapCard = z.infer<typeof recapCardSchema>;

/** The recap page. Mirrors the RecapLab sections. */
export const recapSchema = z.object({
  id: z.string(),
  headline: z.string().max(160),
  nextMoves: z
    .array(
      z.object({
        title: z.string().max(40),
        body: z.string().max(140),
        /** Catalog hero — a move with no groundable image is dropped server-side. */
        imageUrl: z.string(),
        /** Query to send when tapped. */
        query: z.string(),
      }),
    )
    .max(3),
  landed: z
    .object({
      slug: z.string(),
      title: z.string(),
      blurb: z.string().max(140),
      imageUrl: z.string().nullable(),
      rating: z.number().nullable(),
      priceMad: z.number().nullable(),
      reservable: z.boolean(),
    })
    .optional(),
  shortlist: z
    .array(z.object({ slug: z.string(), title: z.string(), imageUrl: z.string().nullable() }))
    .max(6),
  nextSteps: z
    .array(z.object({ label: z.string().max(60), query: z.string() }))
    .max(4),
});
export type Recap = z.infer<typeof recapSchema>;

/** POST /ai/recaps/:id/resume — context to preload into the chat. */
export const recapResumeSchema = z.object({
  /** Context pill text, e.g. "Context loaded — Riad Yasmine, 2 nights". */
  context: z.string(),
  /** Recent turns to seed the client conversation with. */
  turns: z.array(
    z.object({ role: z.enum(["user", "assistant"]), content: z.string() }),
  ),
});
export type RecapResume = z.infer<typeof recapResumeSchema>;
